import { Coordinates } from 'src/interfaces/idashboard';

// Function to calculate bounds from polygons
export const calculateBoundsAll = (polygonPaths: Coordinates[][][]) => {
  if (!polygonPaths.length) return null;

  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;

  polygonPaths.forEach(polygon => {
    polygon.forEach(path => {
      path.forEach(coord => {
        minLat = Math.min(minLat, coord.lat);
        maxLat = Math.max(maxLat, coord.lat);
        minLng = Math.min(minLng, coord.lng);
        maxLng = Math.max(maxLng, coord.lng);
      });
    });
  });

  return {
    north: maxLat,
    south: minLat,
    east: maxLng,
    west: minLng,
  };
};

// Function to calculate zoom level based on bounds
export const calculateZoomLevel = (bounds: any, mapWidth = 800, mapHeight = 500) => {
  if (!bounds) return 12;

  const WORLD_DIM = { height: 256, width: 256 };
  const ZOOM_MAX = 21;

  function latRad(lat: number) {
    const sin = Math.sin((lat * Math.PI) / 180);
    const radX2 = Math.log((1 + sin) / (1 - sin)) / 2;
    return Math.max(Math.min(radX2, Math.PI), -Math.PI) / 2;
  }

  function zoom(mapPx: number, worldPx: number, fraction: number) {
    return Math.floor(Math.log(mapPx / worldPx / fraction) / Math.LN2);
  }

  const latFraction = (latRad(bounds.north) - latRad(bounds.south)) / Math.PI;
  const lngDiff = bounds.east - bounds.west;
  const lngFraction = lngDiff < 0 ? lngDiff + 360 : lngDiff;
  const lngFractionNormalized = lngFraction / 360;

  const latZoom = zoom(mapHeight, WORLD_DIM.height, latFraction);
  const lngZoom = zoom(mapWidth, WORLD_DIM.width, lngFractionNormalized);

  return Math.min(latZoom, lngZoom, ZOOM_MAX) - 1; // Subtract 1 for padding
};

export const getSubmarketCoordsById = (submarketId: string, submarkets: any[]) => {
  const submarket = [...submarkets].find(submarket => submarket.submarket_id == submarketId);
  return submarket ? { center: submarket.center, boundary: submarket.boundary } : null;
};

export const getSubmarketNameById = (
  submarketId: string,
  submarkets: any,
  extraFields: string[] | null = null
) => {
  if (!submarkets || !submarkets.length || !Array.isArray(submarkets)) return null;
  const submarket = [...submarkets].find(submarket => submarket.submarket_id == submarketId);

  if (extraFields) {
    const result = {
      submarket_name: submarket.submarket_name,
    };
    extraFields.forEach(field => {
      result[field] = submarket[field] || null;
    });
    return result;
  }

  return submarket ? submarket.submarket_name : null;
};

export const filterOutHiddenSubmarkets = (
  submarkets: any[],
  hiddenSubmarkets: string[],
  newlyAddedSubmarketId: string | null = null
) => {
  let filteredHiddenSubmarkets: any[] = [];

  if (hiddenSubmarkets.length) {
    filteredHiddenSubmarkets = submarkets.filter(
      submarket => !hiddenSubmarkets.includes(String(submarket.submarket_id))
    );
  } else {
    filteredHiddenSubmarkets = submarkets;
  }

  if (!newlyAddedSubmarketId) {
    return filteredHiddenSubmarkets;
  }

  let submarketsIncludingNewlyAdded: any[] = [];
  const newlyAdddSubmarketFromSubmarketList = filteredHiddenSubmarkets.find(
    submarket => submarket.submarket_id == newlyAddedSubmarketId
  );

  if (newlyAdddSubmarketFromSubmarketList) {
    submarketsIncludingNewlyAdded = [
      newlyAdddSubmarketFromSubmarketList,
      ...filteredHiddenSubmarkets.filter(
        submarket => submarket.submarket_id != newlyAddedSubmarketId
      ),
    ];
  } else {
    submarketsIncludingNewlyAdded = filteredHiddenSubmarkets;
  }

  return submarketsIncludingNewlyAdded;
};


export const getTop3Submarkets = (submarkets: any) => {
  let top3Submarkets: any[] = [];
  const adjustedSubmarkets: any = {};

  if (submarkets && submarkets !== 'loading' && Array.isArray(submarkets)) {
    top3Submarkets = [...submarkets].filter(submarket => submarket.neighborhood_grade).slice(0, 3);
  }

  top3Submarkets.forEach(submarket => {
    adjustedSubmarkets[submarket.submarket_id] = true;
  });

  if (top3Submarkets.length < 3) {
    let missingSubmarketsCount = 3 - top3Submarkets.length;
    top3Submarkets = [
      ...top3Submarkets,
      ...submarkets
        .filter(submarket => !adjustedSubmarkets[submarket.submarket_id])
        .slice(0, missingSubmarketsCount),
    ];
  }

  return top3Submarkets;
};

export const wrapLabel = (text: string, maxChars = 12): string[] => {
  const words = text.split(' ');
  const lines: string[] = [];
  let currentLine = '';

  words.forEach(word => {
    if (word.length > maxChars) {
      // flush the current line if it has content
      if (currentLine) {
        lines.push(currentLine.trim());
        currentLine = '';
      }
      // break long word into chunks
      const chunks = word.match(new RegExp(`.{1,${maxChars}}`, 'g')) || [];
      lines.push(...chunks);
    } else {
      // check if we can add this word to the current line
      if ((currentLine + ' ' + word).trim().length <= maxChars) {
        currentLine += (currentLine ? ' ' : '') + word;
      } else {
        lines.push(currentLine.trim());
        currentLine = word;
      }
    }
  });

  if (currentLine) {
    lines.push(currentLine.trim());
  }

  return lines;
};

export interface Poi {
  name: string;
  poi_type: string;
  lat: number | null;
  lng: number | null;
  radius_m: number | null;
  listing_count: number | null;
  avg_premium_factor: number | string | null;
}

/** Geocoded POIs sorted by premium desc; free users get "locked" premiums, so fall back to listing count. */
export const sortPois = (pois: Poi[]): Poi[] => {
  if (!pois || !Array.isArray(pois)) return [];
  const validPois = pois.filter(poi => poi.lat && poi.lng);

  return [...validPois].sort((a, b) => {
    const aPremium = typeof a.avg_premium_factor === 'number' ? a.avg_premium_factor : null;
    const bPremium = typeof b.avg_premium_factor === 'number' ? b.avg_premium_factor : null;
    if (aPremium === null && bPremium === null) {
      return (b.listing_count || 0) - (a.listing_count || 0);
    }
    if (aPremium === null) return 1;
    if (bPremium === null) return -1;
    return bPremium - aPremium;
  });
};

export const formatPoiType = (poiType: string) => {
  if (!poiType) return '';
  return poiType
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

export const formatRadius = (radiusM: number | null) => {
  if (!radiusM) return '-';
  if (radiusM < 1000) return `${radiusM}m`;
  const km = radiusM / 1000;
  return `${Number.isInteger(km) ? km : km.toFixed(1)}km`;
};

export const formatPremium = (premium: number) =>
  `${premium >= 0 ? '+' : ''}${Math.round(premium)}%`;

const geoDistanceM = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
  const dx = (lng2 - lng1) * Math.cos(((lat1 + lat2) / 2) * Math.PI / 180) * 111320;
  const dy = (lat2 - lat1) * 111320;
  return Math.sqrt(dx * dx + dy * dy);
};

/**
 * Returns a Set of indices (0-based) whose labels should be visible.
 * For overlapping circles, only the first (highest-priority) keeps its label.
 */
export const getVisiblePoiLabels = (pois: Poi[]): Set<number> => {
  const visible = new Set<number>();
  for (let i = 0; i < pois.length; i++) {
    const a = pois[i];
    let hidden = false;
    for (const j of visible) {
      const b = pois[j];
      const dist = geoDistanceM(a.lat!, a.lng!, b.lat!, b.lng!);
      if (dist < (a.radius_m || 500) + (b.radius_m || 500)) {
        hidden = true;
        break;
      }
    }
    if (!hidden) visible.add(i);
  }
  return visible;
};

/**
 * Generates a premium-focused summary sentence for the top POIs.
 * Uses *bold* markers compatible with parseText / parseLockTextIntoIcon.
 */
export const generatePoiSummary = (pois: Poi[], isSubmarketAccessAllowed: boolean): string => {
  const sorted = sortPois(pois);
  if (!sorted.length) return '';

  const withPremium = sorted.filter(
    p => typeof p.avg_premium_factor === 'number' && p.avg_premium_factor > 0
  );
  const hasLocked = sorted.some(p => p.avg_premium_factor === 'locked');

  if (hasLocked && !isSubmarketAccessAllowed) {
    const top = sorted.slice(0, 2);
    if (top.length === 1) {
      return `Guests pay a location premium near *${top[0].name}* (${formatPoiType(top[0].poi_type)}). Upgrade to see exactly how much more guests pay at each hotspot — *locked*.`;
    }
    return `Guests pay a location premium near *${top[0].name}* and *${top[1].name}*. Upgrade to see exactly how much more guests pay at each hotspot — *locked*.`;
  }

  if (withPremium.length === 0) {
    const top = sorted.slice(0, 2);
    if (top.length === 1) {
      return `*${top[0].name}* (${formatPoiType(top[0].poi_type)}) is a notable hotspot with ${top[0].listing_count ?? 'several'} active listings nearby.`;
    }
    return `*${top[0].name}* and *${top[1].name}* are notable hotspots with ${top[0].listing_count ?? 'several'} and ${top[1].listing_count ?? 'several'} active listings nearby, respectively.`;
  }

  const top = withPremium.slice(0, 3);

  if (top.length === 1) {
    return `Guests pay *${formatPremium(top[0].avg_premium_factor as number)} more* near *${top[0].name}* (${formatPoiType(top[0].poi_type)}), making it the top-performing hotspot in this market.`;
  }

  const highlights = top
    .map(p => `*${formatPremium(p.avg_premium_factor as number)}* near *${p.name}*`)
    .join(', ');

  const suffix =
    sorted.length > top.length
      ? ` Explore all ${sorted.length} hotspots to find the best location for your investment.`
      : '';

  return `Guests pay a location premium of ${highlights}.${suffix}`;
};

export const checkIfMarketTrackedAlready = (
  eventType: 'submarkets_tab_viewed' | 'submarket_cta_viewed' | 'hotspot_analysis_viewed',
  marketID: number
) => {
  const localStorageKey = `tracked_${eventType}`;
  const trackedMarkets = localStorage.getItem(localStorageKey);
  let trackedMarketsArray: any = {};

  if (trackedMarkets) {
    trackedMarketsArray = JSON.parse(trackedMarkets);
    if (trackedMarketsArray[marketID]) return true;
  }

  trackedMarketsArray[marketID] = true;
  localStorage.setItem(localStorageKey, JSON.stringify(trackedMarketsArray));

  return false;
};
